import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { DebugBox, LoadingBox, useNavigateWithParams } from "@northpowerdev/np-react-utils";
import { useEffect } from "react";
import { plural } from "../../../utils/utilityFunctions";
import useFetchJson from "../../../data/useFetchJson";
import { useGetParams } from "../../../utils/useGetParams";
import { DlspBreadcrumbs } from "./DlspBreadcrumbs";
import { useState } from "react";
import { useContext } from "react";
import { MahiContext, mahiRolesFromMahiContext } from "../../state/MahiContext";
import { AssigneeSelect } from "./AssigneeSelect";
import DlspSuburbTaskListTabs from "./DlspSuburbTaskListTabs";

export default function DlspSuburbTaskList() {

    const [bulkAssignmentCounter, setBulkAssignmentCounter] = useState(0)
    const getParams = useGetParams(['storm_area', 'suburb', 'assignee', 'status'])
    const navigateToUpdateAssignee = useNavigateWithParams('/dlsp/suburb_task_list')

    const getTaskListPath = '/bff/decommissioned-lines-service/api/decommissioned-lines/list-all'
    const {
        isLoading: taskListLoading,
        data: taskList, // [{ decommissionReason, decommissionDate, decommissionStatus, premiseType, address, addressNotes, dsub, dsubSuburb, stormArea, flag, icpOrientation, lvOrientation, icp, status, assignee, desktopInspBy, desktopInspDate, fieldInspBy, fieldInspDate}, ...]
        load: getTaskList} = useFetchJson(getTaskListPath, {}, false) 

    useEffect(() => {
        if (!getParams || !getParams.storm_area || !getParams.suburb) {
            return
        }
        // console.log('DlspSuburbTaskList useEffect got storm_area, suburb', getParams.storm_area, getParams.suburb)
        const pageIdx = 0
        const pageSize = 1000
        const taskListParams = {
            params: {
                stormArea: getParams.storm_area,
                suburb: getParams.suburb,
                pageIdx,
                pageSize
            }
        }
        if (getParams.assignee) {
            taskListParams.params.assignee = getParams.assignee
        }

        getTaskList(taskListParams, getTaskListPath) // async

    }, [getParams, getTaskList, bulkAssignmentCounter]) 

    const handleTasksAssigned = async (newAssigneeName) => {
        setBulkAssignmentCounter(value => value + 1) // trigger useEffect to re-load task list
    }

    const handleAssigneeSelected = (newAssignee) => {
        console.log('DlspSuburbTaskList handleAssigneeSelected newAssignee', newAssignee)
        navigateToUpdateAssignee({...getParams, assignee: newAssignee} )
    }

    return (
        <Box className="Page">
            <h3>Decommissioned Lines Safety Project</h3>
            <h2>Summary for suburb {getParams?.suburb}</h2>

            <DlspBreadcrumbs
                assignee={getParams?.assignee}
                stormArea={getParams?.storm_area}
                suburb={getParams?.suburb}
            />

            {
                getParams?.suburb === ''
                ?
                <Alert severity="warning">Please provide a suburb.</Alert>
                :
                <LoadingBox loading={!taskList || taskListLoading}>
                    <Stack direction="row" sx={{justifyContent: 'space-between', alignItems: 'top'}}>
                        <Typography variant="body1">We found {taskList?.length} task{plural(taskList?.length)}.</Typography>
                        <Box>
                            <AssigneeSelect assignee={getParams?.assignee || ''} onChange={handleAssigneeSelected} />
                            <DlspAssignTasksBySuburbForm
                                stormArea={getParams?.storm_area}
                                suburb={getParams?.suburb}
                                taskCount={taskList?.length || 0}
                                onAssigned={handleTasksAssigned}
                            />
                        </Box>
                    </Stack>
                    <DlspSuburbTaskListTabs taskList={taskList} assignee={getParams?.assignee} selectedTab={getParams?.status} />

                    <DebugBox title="getParams?.assignee" stringify>{getParams?.assignee}</DebugBox>
                    <DebugBox title="taskList" stringify>{taskList}</DebugBox>
                </LoadingBox>
            }




        </Box>
    )
}

/**
 * 
 * @param {object} props E.g. {
 *   stormArea {string} "Whg North"
 *   suburb {string} "Kauri"
 *   taskCount {number} 43
 *   onAssigned {function} Called with assigneeName after assignment is made.
 * } 
 * @returns JSX
 */
function DlspAssignTasksBySuburbForm({stormArea, suburb, taskCount, onAssigned}) {

    const [open, setOpen] = useState(false)
    const [selectedInspectorName, setSelectedInspectorName] = useState('')
    const {
        data: desktopInspectorMahiUsers // E.g. [ { "id", "oid", "name", "email", "phone", "roles"; [], "lastLoginDate", "active", "account": {...} }, ... ]
    } = useFetchJson('/bff/decommissioned-lines-service/api/decommissioned-lines/get-inspectors', {}, true)

    const {
        load: postBulkAssignDesktop,
        isLoading: isPostingBulkAssign
    } = useFetchJson('/bff/decommissioned-lines-service/api/decommissioned-lines/bulk-assign-desktop', {method: 'POST'})

    const mahiContext = useContext(MahiContext)
    const mahiRoles = mahiRolesFromMahiContext(mahiContext)
    const isAdmin = mahiRoles.includes('Mahi.DecommissionedLines.Admin')

    if (!suburb || !taskCount) {
        return null
    }

    const onClose = () => {
        setOpen(false)
    }

    const handleAssignToInspector = async (e) => {
        e.preventDefault()
        const options = {
            body: JSON.stringify({
                stormArea: stormArea,
                dsubSuburb: suburb,
                userName: selectedInspectorName
            }),
        }
        await postBulkAssignDesktop(options)
        onClose()
        onAssigned(selectedInspectorName) // tell caller we have assigned tasks to the selected inspector
    }

    return (
        <Box>
            <Button
                    variant="text"
                    disabled={!isAdmin}
                    onClick={() => setOpen(true)}>
                Bulk Assign {taskCount} Task{plural(taskCount)}
            </Button>

            <Dialog open={open} onClose={() => onClose()}>
                <DialogTitle>Bulk Assign {taskCount} Task{plural(taskCount)} for {suburb} in {stormArea}</DialogTitle>
                <DialogContent>
                    <Box sx={{my: 4}}>
                        <form onSubmit={handleAssignToInspector}>
                            <Box sx={{my:1}}>
                                <TextField
                                    select
                                    label="Selected Inspector"
                                    value={selectedInspectorName}
                                    helperText="Please select your desktop inspector"
                                    onChange={(e) => { setSelectedInspectorName(e.target.value)}}
                                >
                                    {
                                        desktopInspectorMahiUsers?.map(u => {
                                            return (
                                                <MenuItem key={u.name} value={u.name}>{u.name}</MenuItem>
                                            )
                                        })
                                    }

                                </TextField>
                            </Box>
                            <LoadingBox loading={isPostingBulkAssign}>
                                <Button type="submit" disabled={!selectedInspectorName} sx={{mr:2}} variant="contained">Assign Tasks to Inspector</Button>
                            </LoadingBox>
                        </form>
                        {/* <DebugBox title="desktopInspectorNameList" stringify>{desktopInspectorMahiUsers}</DebugBox> */}
                    </Box>
                </DialogContent>
                <DialogActions>
                        <Button onClick={() => onClose()} autoFocus>Close</Button>
                </DialogActions>
            </Dialog>

        </Box>
    )
}
